<template>
  <panel class="mt-6 pt-6">
    <loader v-if="loading" />

    <div v-else-if="!settings" class="flex justify-center items-center my-12">
      <h2 class="text-grey-light text-4xl font-normal">No Engage Instance Found</h2>
    </div>

    <div v-else class="text-sm">
      <h2 class="text-base mb-6">Settings</h2>

      <label class="mb-3 block uppercase">Enabled Departments:</label>
      <div class="mb-2">
        <input type="checkbox" id="sales" v-model="settings.sales">
        <label for="sales">Sales</label>
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('sales')" v-text="errorBag.get('sales')"></small>
      </div>

      <div class="mb-6">
        <input type="checkbox" id="lettings" v-model="settings.lettings">
        <label for="lettings">Lettings</label>
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('lettings')" v-text="errorBag.get('lettings')"></small>
      </div>

      <label class="mb-3 block uppercase">Experience:</label>
      <div class="mb-2">
        <input type="radio" id="simple" name="experience" v-model="settings.experience_id" :value="0">
        <label for="simple">Simple (One page, quick lead capture)</label>
      </div>

      <div class="mb-6">
        <input type="radio" id="extended" name="experience" v-model="settings.experience_id" :value="1">
        <label for="extended" class="whitespace-normal">Extended (Multi-page with additional capture fields for up-front property information)</label>
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('lettings')" v-text="errorBag.get('lettings')"></small>
      </div>

      <div v-if="selectedOffice.network_id === 2">
        <label class="mb-3 block uppercase">Referral System:</label>
        <div class="mb-6">
          <input type="checkbox" id="referrals" v-model="settings.referrals_enabled">
          <label for="referrals">Send out-of-area leads to the referral system</label>
          <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('referrals_enabled')" v-text="errorBag.get('referrals_enabled')"></small>
        </div>
      </div>

      <label class="mb-3 block uppercase">Lead Delivery:</label>
      <div class="mb-2">
        <input type="radio" id="leads1" name="leads" v-model="settings.send_leads_to_offices" :value="true">
        <label for="leads1">Send leads to the nearest office by shortest distance to post code</label>
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('send_leads_to_offices')" v-text="errorBag.get('send_leads_to_offices')"></small>
      </div>

      <div class="mb-6">
        <input type="radio" id="leads0" name="leads" v-model="settings.send_leads_to_offices" :value="false">
        <label for="leads0">Send leads to a specified email address</label>
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('send_leads_to_offices')" v-text="errorBag.get('send_leads_to_offices')"></small>
      </div>

      <div class="mb-6" v-if="!settings.send_leads_to_offices">
        <label for="email" class="uppercase">Send leads to this email address:</label>
        <input type="email" id="email" v-model="settings.email_to" class="md:w-1/2 block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('email_to') }">
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('email_to')" v-text="errorBag.get('email_to')"></small>
      </div>

      <div class="mb-6">
        <label for="email" class="uppercase mb-2 block">Also send lead notifications to:</label>
        <em class="block mb-3 text-grey" v-if="settings.email_cc.length === 0">No additional email addresses.</em>
        <div v-for="(cc, i) in settings.email_cc" :key="i" class="mb-2">
          {{ cc }} <i class="fas fa-times-circle hover:text-red-dark cursor-pointer" title="Remove CC" @click="removeCc(cc)"></i>
          <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('email_cc.' + i)" v-text="errorBag.get('email_cc.' + i)"></small>
        </div>
        <div class="flex flex-wrap">
          <input type="email" id="email" v-model="newCc" class="md:w-1/2 block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm">
          <button class="bg-grey-lightest border py-1 px-6 ml-3 hover:border-grey" @click="addCc">Add CC Email Address</button>
        </div>
      </div>

      <div class="mb-6">
        <label for="privacy" class="uppercase">Privacy Policy:</label>
        <textarea id="privacy" v-model="settings.privacy_policy" class="md:w-1/2 h-32 block w-full bg-grey-lighter border hover:border-grey focus:border-grey-dark p-2 mt-2 h-10 text-sm" :class="{ 'border-red-dark hover:border-red-dark focus:border-red-dark': errorBag.get('privacy_policy') }"></textarea>
        <small class="block text-red-dark text-medium mt-2" v-if="errorBag.get('privacy_policy')" v-text="errorBag.get('privacy_policy')"></small>
      </div>

      <div class="flex">
        <button
          class="bg-secondary text-white rounded px-6 py-2 w-1/2 md:w-1/6"
          :class="{ 'opacity-75': saving }"
          :disabled="saving"
          @click="saveSettings"
        >
          {{ saving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </panel>
</template>

<script>
import api from '@/http'
import { mapState, mapActions } from 'vuex'
import ErrorBag from '@/ErrorBag'

export default {
  data () {
    return {
      loading: true,
      saving: false,
      settings: {},
      newCc: '',
      errorBag: new ErrorBag()
    }
  },

  computed: {
    ...mapState(['selectedOffice'])
  },

  methods: {
    ...mapActions(['displayToastNotification']),

    fetchSettings () {
      if (!this.selectedOffice.id) {
        return
      }

      this.loading = true
      api
        .get(`office/${this.selectedOffice.id}/engage`)
        .then(({ data }) => {
          this.loading = false
          this.settings = data
        })
        .catch(() => {
          this.loading = false
          this.settings = null
        })
    },

    saveSettings () {
      this.saving = true
      this.errorBag.clear()

      api
        .put(`office/${this.selectedOffice.id}/engage`, this.settings)
        .then(({ data }) => {
          this.saving = false
          this.settings = data
          this.displayToastNotification({
            type: 'success',
            icon: 'fa-check-circle',
            title: 'Engage Settings Saved',
            info: 'Your instance settings have been successfully saved.'
          })
        })
        .catch(({ response }) => {
          this.saving = false
          this.errorBag.store(response.data)
          this.displayToastNotification({
            type: 'danger',
            icon: 'fa-times-circle',
            title: 'Error',
            info: response.data.message
          })
        })
    },

    addCc () {
      this.settings.email_cc.push(this.newCc)
      this.newCc = ''
    },

    removeCc (cc) {
      let index = this.settings.email_cc.indexOf(cc)
      this.settings.email_cc.splice(index, 1)
    }
  },

  created () {
    this.fetchSettings()
  },

  watch: {
    selectedOffice () {
      this.fetchSettings()
    }
  }
}
</script>

<style lang="scss" scoped>

</style>
